import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import { CATEGORY_LABEL, RAIL_LABEL, formatINR } from "@/lib/constants";
import { toast } from "sonner";

const PERIODS = [
  { id: "today", label: "Today" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
];

export default function Reports() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (p) => {
    setLoading(true);
    try {
      const res = await api.get(`/transactions/summary?period=${p}`);
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(period); }, [period]);

  const chartData = (data?.by_category || []).map((c) => ({
    name: CATEGORY_LABEL[c.category] || RAIL_LABEL[c.category] || c.category,
    volume: c.volume,
    earnings: c.earnings,
    count: c.count,
  }));

  const successRate = data && data.total_transactions > 0
    ? Math.round((data.successful_transactions / data.total_transactions) * 100)
    : 0;

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Analytics</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Reports &amp; commission tracking
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Volume, success rate, earnings and commission breakdown by category and rail.
          </p>
        </div>
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList data-testid="period-tabs">
            {PERIODS.map((p) => (
              <TabsTrigger key={p.id} value={p.id} data-testid={`period-${p.id}`}>{p.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
        <Kpi label="Transactions" value={data?.total_transactions || 0} mono />
        <Kpi label="Volume" value={formatINR(data?.total_volume || 0)} mono />
        <Kpi label="Earnings" value={formatINR(data?.total_earnings || 0)} mono success />
        <Kpi label="Success rate" value={`${successRate}%`} mono />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 surface-card p-6">
          <span className="overline">Volume by category / rail</span>
          <div className="mt-5 h-72" data-testid="report-chart">
            {chartData.length === 0 ? (
              <div className="h-full grid place-items-center text-muted-foreground text-sm">
                {loading ? "Loading…" : "No transactions in this period yet."}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                  <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${Math.round(v / 1000)}k`} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                    formatter={(v, k) => k === "count" ? v : formatINR(v)}
                  />
                  <Bar dataKey="volume" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]}>
                    {chartData.map((_, i) => <Cell key={i} fill={i % 2 === 0 ? "hsl(var(--primary))" : "hsl(var(--secondary))"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="surface-card p-6">
          <span className="overline">Earnings by category</span>
          <ul className="mt-5 divide-y divide-border" data-testid="earnings-list">
            {chartData.length === 0 ? (
              <li className="text-sm text-muted-foreground py-4">Nothing yet.</li>
            ) : (
              chartData.map((c) => (
                <li key={c.name} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium text-sm">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.count} txn · {formatINR(c.volume)}</div>
                  </div>
                  <span className="font-mono tabular-nums text-success">{formatINR(c.earnings)}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      <div className="mt-8 surface-card p-6">
        <span className="overline">By type</span>
        <div className="mt-5 grid sm:grid-cols-2 gap-6">
          {["bbps", "payout"].map((t) => (
            <div key={t} data-testid={`type-block-${t}`} className="flex items-baseline justify-between py-4 border-b border-border">
              <div>
                <div className="font-display text-lg font-bold uppercase">{t}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {data?.by_type?.[t]?.count || 0} transactions · {formatINR(data?.by_type?.[t]?.volume || 0)} volume
                </div>
              </div>
              <div className="font-mono tabular-nums text-success text-xl">
                {formatINR(data?.by_type?.[t]?.earnings || 0)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Kpi({ label, value, mono, success }) {
  return (
    <div className="surface-card p-5 interactive-card relative overflow-hidden">
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-primary/5" />
      <span className="overline relative">{label}</span>
      <div className={`relative ${mono ? "font-mono" : ""} text-2xl mt-2 ${success ? "text-success" : ""}`}>{value}</div>
    </div>
  );
}
